import { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Search, Plus, Edit, Trash2, Tag } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import TenantLists from "../TenantLists";
import useUserStore from "../../../services/userStore";
import { createTags, deleteTag, getAllTags, updateTag } from "./Api/tagdetailsapi";
import { Skeleton } from "../../ui/skeleton"; // ✅ Added import
import { formatDate } from "../formatTimeAgo";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { myLocalStorage } from "../../StorageHelper";
import EmptyStateCard from "../EmptyStateCard";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
    useSkipInitialPageReset,
} from "../../../utils/listPaginationState";

interface TagData {
    id: string;
    name: string;
    tenantName: string;
    description?: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
}

export default function TagsDetails() {
    const savedState = readPersistedListState(LIST_STATE_KEYS.tags);
    const { toast } = useToast();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(parsePersistedString(savedState.selectedTenant));
    const [searchQuery, setSearchQuery] = useState(parsePersistedString(savedState.searchQuery));
    const [tagName, setTagName] = useState("");
    const [tagDescription, setTagDescription] = useState("");
    const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedState.currentPage, 1));
    const runTenantChangeWithPageReset = useSkipInitialPageReset(setCurrentPage);
    const { email } = useUserStore((state) => state.user);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [tags, setTags] = useState<TagData[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<TagData>();
    const [isEdit, setIsEdit] = useState<boolean>(false)
    const userData = useUserStore((state) => state.user);
    const storedRole = myLocalStorage.getItem("role");
    const normalizedRole = (userData?.userRole || userData?.role || storedRole || "").toLowerCase();
    const hasElevatedTagPrivileges =
        normalizedRole === "whiteswan-admin" ||
        userData?.email?.startsWith("wsadmin");

    const TAGS_PER_PAGE = 15;

    const fetchTags = async () => {
        try {
            setLoading(true);
            let response = await getAllTags(selectedTenant, currentPage - 1, searchQuery);
            const sorted = [...response.content];
            setTags(sorted);
            setTotalPages(response.totalPages ?? 0);
            setTotalElements(response.totalElements ?? sorted.length);
        } catch (error) {
            console.error("Error fetching tags:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTag = async () => {
        if (!tagName.trim()) {
            toast({
                title: "Error",
                description: "Tag name is required",
                variant: "destructive"
            });
            return;
        }

        const newTag: TagData = {
            id: Date.now().toString(),
            name: tagName.trim(),
            tenantName: selectedTenant,
            description: tagDescription.trim() || "",
            createdBy: email,
            createdAt: new Date().toLocaleString(),
            updatedBy: email,
            updatedAt: new Date().toLocaleString()
        };

        await createTags({
            name: tagName.trim(),
            tenantName: selectedTenant,
            description: tagDescription.trim() || ""
        });
        fetchTags();
        setTags(prev => [...prev, newTag]);
        setTagName("");
        setTagDescription("");
        setIsCreateDialogOpen(false);

        toast({
            title: "Tag Created",
            description: `Tag "${newTag.name}" has been created successfully.`
        });
    };

    const openEditDialog = (tag: TagData) => {
        setIsEdit(true)
        setSelectedTag(tag);
        setTagName(tag.name);
        setTagDescription(tag.description || "");
        setIsCreateDialogOpen(true)
    }


    const handleEditTag = async () => {

        if (!tagName.trim()) {
            toast({
                title: "Error",
                description: "Tag name is required",
                variant: "destructive"
            });
            return;
        }

        await updateTag(selectedTag?.id || "-1", tagName.trim(), selectedTenant, tagDescription.trim() || "");

        toast({
            title: "Edit Tag",
            description: "Edited successfully"
        });
        const updatedTag = {
            ...selectedTag,
            name: tagName.trim(),
            description: tagDescription.trim() || "",
            tenant: selectedTenant,
        };

        setTags((prev) =>
            prev.map((tag) =>
                tag.id === selectedTag?.id ? { ...tag, ...updatedTag } : tag
            )
        );

        setTagName("");
        setTagDescription("");

        setIsCreateDialogOpen(false);
        setSelectedTag(undefined)
        setIsEdit(false)
    };

    const handleSelectTag = (tag: TagData) => {
        setSelectedTag(tag);
        setShowDeleteConfirmation(true)
    }


    const handleDeleteTag = async (tagId: string, tagName: string) => {
        await deleteTag(tagId);
        setTags(prev => prev.filter(tag => tag.id !== tagId));
        setShowDeleteConfirmation(false);
        setSelectedTag(undefined);
        toast({
            title: "Tag Deleted",
            description: `Tag "${tagName}" has been deleted successfully.`
        });
    };

    useEffect(() => {
        if (!selectedTenant) return;
        fetchTags();
    }, [currentPage, searchQuery, selectedTenant]);

    useEffect(() => {
        writePersistedListState(LIST_STATE_KEYS.tags, {
            currentPage,
            searchQuery,
            selectedTenant,
        });
    }, [currentPage, searchQuery, selectedTenant]);


    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Header */}
            <div className="bg-slate-50 dark:bg-slate-900/50 border-b px-8 py-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                        <Tag className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Global Tags Management</h1>
                        <p className="text-base text-muted-foreground">
                            Create, organize, and manage tags
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 px-8 py-5">
                <Card className="shadow-sm">
                    <CardHeader className="bg-white dark:bg-slate-800/50 border-b">
                        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                            {/* Left Controls */}
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <div className="flex items-center gap-3">
                                    <TenantLists onSelectorChange={(tenant: string) => {
                                        runTenantChangeWithPageReset(() => setSelectedTenant(tenant));
                                    }} isLabelNeeded={true} />
                                </div>

                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search tags by name or creator..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="pl-10 w-72 h-9 text-sm"
                                    />
                                </div>
                            </div>

                            {/* Right Actions */}
                            <div className="flex items-center gap-3">
                                <div className="text-xs text-muted-foreground">
                                    {totalElements} {totalElements === 1 ? "tag" : "tags"} found
                                </div>
                                <Button
                                    onClick={() => {
                                        setIsEdit(false)
                                        setIsCreateDialogOpen(true)
                                    }}
                                    className="gap-1.5 h-9 px-4"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create New Tag
                                </Button>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="p-6">
                        {loading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <Card key={i} className="border shadow-sm p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="w-6 h-6 rounded" />
                                                <Skeleton className="w-12 h-4" />
                                            </div>
                                            <Skeleton className="w-10 h-4" />
                                        </div>
                                        <Skeleton className="w-3/4 h-5 mt-2" />
                                        <Skeleton className="w-1/2 h-4" />
                                        <Skeleton className="w-32 h-4" />
                                        <Skeleton className="w-full h-12 mt-2" />
                                        <div className="flex gap-2">
                                            <Skeleton className="w-1/2 h-8 rounded" />
                                            <Skeleton className="w-1/2 h-8 rounded" />
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        ) : tags.length === 0 ? (
                            <EmptyStateCard
                                icon={Tag}
                                title={searchQuery ? "No tags match your search" : "No tags created yet"}
                                description={
                                    searchQuery
                                        ? "Try adjusting your search terms or create a new tag."
                                        : "Create your first global tag to organize endpoints and resources."
                                }
                                action={
                                    !searchQuery ? (
                                        <Button
                                            onClick={() => {
                                                setIsEdit(false);
                                                setIsCreateDialogOpen(true);
                                            }}
                                            className="gap-1.5"
                                            size="sm"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Create Your First Tag
                                        </Button>
                                    ) : undefined
                                }
                            />
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {tags.map((tag) => (
                                        <Card
                                            key={tag.id}
                                            className="relative border hover:shadow-md transition-all"
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/20 rounded flex items-center justify-center">
                                                            <Tag className="w-4 h-4 text-blue-600" />
                                                        </div>
                                                        <span className="text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                                                            Global
                                                        </span>
                                                    </div>
                                                </div>

                                                <h3 className="font-semibold text-sm mb-2">{tag.name}</h3>
                                                <div className="text-xs text-muted-foreground mb-1">
                                                    Created by: {tag.createdBy}
                                                </div>
                                                <div className="text-xs text-muted-foreground mb-3">
                                                    Date: {formatDate(tag.createdAt)}
                                                </div>
                                                <div className="text-xs text-muted-foreground mb-4 line-clamp-2">
                                                    {tag.description || "No description provided"}
                                                </div>

                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openEditDialog(tag)}
                                                        className="h-7 px-2 text-xs flex-1"
                                                        disabled={
                                                            !(hasElevatedTagPrivileges || userData?.email === tag.createdBy)
                                                        }
                                                    >
                                                        <Edit className="w-3 h-3 mr-1" />
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleSelectTag(tag)}
                                                        className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 flex-1"
                                                        disabled={
                                                            !(hasElevatedTagPrivileges || userData?.email === tag.createdBy)
                                                        }
                                                    >
                                                        <Trash2 className="w-3 h-3 mr-1" />
                                                        Delete
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                                        <div className="text-sm text-muted-foreground">
                                            Showing {(currentPage - 1) * TAGS_PER_PAGE + 1} to {Math.min(currentPage * TAGS_PER_PAGE, totalElements)} of {totalElements} tags
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="h-8 px-3"
                                            >
                                                Previous
                                            </Button>
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                <Button
                                                    key={page}
                                                    variant={currentPage === page ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => setCurrentPage(page)}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    {page}
                                                </Button>
                                            ))}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="h-8 px-3"
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Dialog for Creating Tags */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-xl">
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                                <Tag className="w-5 h-5 text-blue-600" />
                            </div>
                            {!isEdit ? "Create New Global Tag" : "Edit Global Tag"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 py-6">
                        <div className="space-y-3">
                            <Label htmlFor="tag-name" className="text-base font-semibold">Tag Name *</Label>
                            <Input
                                id="tag-name"
                                placeholder="Enter tag name (e.g., production)"
                                value={tagName}
                                onChange={(e) => setTagName(e.target.value)}
                                className="h-11 text-base"
                            />
                        </div>

                        <div className="space-y-3">
                            <Label htmlFor="tag-description" className="text-base font-semibold">Description</Label>
                            <Textarea
                                id="tag-description"
                                placeholder="Provide context for this tag..."
                                value={tagDescription}
                                onChange={(e) => setTagDescription(e.target.value)}
                                rows={4}
                                className="text-base"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-3">
                        <Button
                            variant="outline"
                            size="lg"
                            onClick={() => {
                                setIsCreateDialogOpen(false);
                                setTagName("");
                                setTagDescription("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button size="lg" onClick={() => {
                            if (isEdit)
                                handleEditTag()
                            else
                                handleCreateTag()
                        }
                        }>
                            {!isEdit ? "Create Tag" : "Edit Tag"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>


            <ConfirmationDialog
                open={showDeleteConfirmation}
                onOpenChange={setShowDeleteConfirmation}
                title="Delete Tag"
                description={`Are you sure, you want to delete this Tag - ${selectedTag?.name}?`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="destructive"
                onConfirm={() => {
                    if (selectedTag)
                        handleDeleteTag(selectedTag.id, selectedTag.name);
                }}
            />
        </div>
    );
}
